// =============================================================================
// Optim TURPE V2 : LES ACTIONS AVANT D'ENVOYER (contrat de l'interface v1.2,
// R-T6, R-T7, §4 « Alerte raccordement », §6 rangée 4, décisions A1 et A7 du
// 18/10/2026 ; champs servis par le lot 4f, courtage#311, v187).
//
// Ce que le courtier doit vivre : l'orange est réservé à un geste à faire
// avant d'envoyer, avec le champ où le faire. La carte de tête en porte une
// seule, au format court ; la rangée « Points à vérifier » les liste toutes,
// au même format court, avec « Corriger » et, quand il ajoute le repli chiffré,
// le texte complet derrière « Voir le détail ». Un constat (qualité des
// mesures, puissance actuelle sous le pic) n'est jamais ici : il se lit en
// gris dans la ligne « Mesures » des hypothèses. La réserve du palier C5 n'est
// pas un point à vérifier (A7) : le repli 12 kVA vit dans la fiche « Pourquoi
// 11 kVA », le courriel et le rapport.
//
// Source : `reserves[].nature` et `bornes.raccordement.alerte` servis. Une
// étude servie avant le lot (sans `nature`) est classée par les mêmes règles
// que le moteur (`reserves_servies.py`), sans aucun texte inventé.
// Module PUR.
// =============================================================================
import { formateurFr } from "@modules/courtage/utils/optim-turpe-v2.nombre"
import { ancreDepuisChamp } from "@modules/courtage/utils/optim-turpe-v2.completer"
import type { AncreAffiner } from "@modules/courtage/utils/optim-turpe-v2.completer"
import { lireRaccordement } from "@modules/courtage/utils/optim-turpe-v2.bornes"
import {
    formatNombreFr,
    getUnitePuissance,
} from "@modules/courtage/utils/optim-turpe-v2.helpers"
import {
    constatCourt,
    sansExtrapolation,
} from "@modules/courtage/utils/optim-turpe-v2.qualite"
import type {
    OptimTurpeV2Etude,
    OptimTurpeV2Reserve,
} from "@modules/courtage/types/optim-turpe-v2"

/** Le chemin du formulaire où se corrige la puissance de raccordement (D19 a). */
export const OU_CORRIGER_RACCORDEMENT =
    "Modifier les hypothèses › Contrat actuel › Puissance de raccordement"

const CODE_RACCORDEMENT_ECARTE = "raccordement-ecarte"
const CODE_BORNE_LEVEE = "raccordement-borne-levee"
const CODE_BASE_IMPOSSIBLE = "montant-non-servi-base-de-comparaison-impossible"
/** A7 : ni le risque C5 ni le palier usuel ne sont des points à vérifier. */
const CODES_JAMAIS_POINT = new Set([
    "c5-pic-au-dessus-de-la-souscrite",
    "puissance-hors-palier-usuel",
])
/** Les réserves qui portent un champ à corriger (`reserves_servies.py`). */
const CODES_ACTION_A_CHAMP = new Set([
    "entree-au-defaut",
    "heures-creuses-non-publiees",
    "modification-contractuelle-recente",
])

/** Le texte de la puissance de raccordement, au format court (A1). */
export type TexteDuRaccordement = {
    /** La ligne courte, la même sur la carte et dans « Points à vérifier ». */
    court: string
    /** Le texte complet servi (trois temps), ou `null`. */
    complet: string | null
    /** Vrai quand le texte complet ajoute le repli chiffré. */
    avecRepli: boolean
    nature: "action" | "constat"
    forme: string | null
}

/** Une action avant d'envoyer, telle que la carte et la rangée la lisent. */
export type PointDAction = {
    code: string
    /** La ligne au format court. */
    court: string
    /** Le texte complet derrière « Voir le détail », quand il dit plus. */
    detail: string | null
    /** Le champ que « Corriger » ouvre, ou `null` (aucun lien). */
    ancre: AncreAffiner | null
    /** Ce que dit le résumé de la rangée (« raccordement à corriger »). */
    resume: string
}

function fini(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur) ? valeur : null
}

/** La plus haute puissance demandée, servie par `demande.puissances`. */
function puissanceDemandeeMax(etude: OptimTurpeV2Etude): string | null {
    const puissances = etude.demande?.puissances ?? []
    const cibles = puissances
        .map((p) => fini(p?.cible))
        .filter((v): v is number => v !== null)
    if (cibles.length === 0) return null
    const unite =
        puissances.find((p) => p?.unite)?.unite ??
        etude.demande?.unite_puissance ??
        getUnitePuissance(etude)
    const nombre = formatNombreFr(Math.max(...cibles), 2)
    return nombre ? `${nombre}\u00a0${unite}` : null
}

/**
 * Le texte de la puissance de raccordement, au format court (décision A1) :
 * le court servi, complété sur une valeur écartée avec démarche par la
 * puissance demandée et le champ à corriger (fait raccordement 4.1). `null`
 * quand rien n'est à dire. Une étude servie avant le lot 4f ne reçoit que
 * l'action d'une valeur écartée sous une puissance demandée au-dessus.
 */
export function texteDuRaccordement(
    etude: OptimTurpeV2Etude | null | undefined
): TexteDuRaccordement | null {
    if (!etude) return null
    const alerte = etude.bornes?.raccordement?.alerte
    const demandee = puissanceDemandeeMax(etude)
    if (alerte && typeof alerte.court === "string" && alerte.court.trim()) {
        const nature = alerte.nature === "action" ? "action" : "constat"
        const ouCorriger =
            alerte.ou_corriger?.trim() || OU_CORRIGER_RACCORDEMENT
        const court =
            alerte.forme === "ecartee-avec-demarche"
                ? `${alerte.court.trim()} ${
                      demandee
                          ? `La demande (${demandee}) suppose`
                          : "La demande suppose"
                  } une étude du gestionnaire de réseau. Corriger la valeur : ${ouCorriger}.`
                : alerte.court.trim()
        const complet = alerte.complet?.trim() || null
        return {
            court,
            complet,
            avecRepli: Boolean(alerte.repli && complet),
            nature,
            forme: alerte.forme ?? null,
        }
    }
    if (alerte !== undefined) return null
    // Étude servie avant le lot 4f : la seule action que la page sait lire.
    const r = lireRaccordement(etude)
    const borne = etude.bornes?.raccordement
    if (
        !r ||
        r.valeur === null ||
        r.statut !== "ecartee" ||
        borne?.recommandation_au_dessus !== true
    )
        return null
    const unite = r.unite ?? getUnitePuissance(etude)
    const valeur = `${formatNombreFr(r.valeur, 2)}\u00a0${unite}`
    const pointe =
        r.pointeMesureeMax !== null
            ? `${formateurFr({
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
              }).format(
                  r.pointeMesureeMax
              )}\u00a0${r.pointeMesureeUnite ?? unite}`
            : null
    const fait = pointe
        ? `${pointe} atteints pour ${valeur} de raccordement selon Enedis : l'étude n'a pas limité les puissances.`
        : `Raccordement selon Enedis (${valeur}) non retenu : l'étude n'a pas limité les puissances.`
    return {
        court: `${fait} ${demandee ? `La demande (${demandee}) suppose` : "La demande suppose"} une étude du gestionnaire de réseau. Corriger la valeur : ${OU_CORRIGER_RACCORDEMENT}.`,
        complet: null,
        avecRepli: false,
        nature: "action",
        forme: "ecartee-avec-demarche",
    }
}

/** La nature d'une réserve : servie, sinon classée comme le moteur la classe. */
function natureDe(
    reserve: OptimTurpeV2Reserve,
    raccordement: TexteDuRaccordement | null
): "action" | "constat" {
    if (reserve.nature === "action" || reserve.nature === "constat")
        return reserve.nature
    if (
        reserve.code === CODE_RACCORDEMENT_ECARTE ||
        reserve.code === CODE_BORNE_LEVEE
    )
        return raccordement?.nature ?? "constat"
    if (reserve.code === CODE_BASE_IMPOSSIBLE) return "action"
    if (CODES_ACTION_A_CHAMP.has(reserve.code) && reserve.champ) return "action"
    return "constat"
}

/**
 * La ligne de la rangée (maquette 2d, P2) : le format court sans le chemin
 * « Corriger la valeur : … », que le bouton « Corriger » remplace.
 */
function sansLeChemin(court: string): string {
    return court.replace(/\s*Corriger la valeur\s*:.*$/s, "").trim()
}

/** « Point à corriger » : ce que dit le résumé de la rangée pour un code. */
function resumeDuCode(code: string): string {
    switch (code) {
        case CODE_RACCORDEMENT_ECARTE:
        case CODE_BORNE_LEVEE:
            return "raccordement à corriger"
        case CODE_BASE_IMPOSSIBLE:
            return "contrat actuel à corriger"
        case "heures-creuses-non-publiees":
            return "heures creuses à confirmer"
        case "modification-contractuelle-recente":
            return "changement de formule récent"
        default:
            return "valeur par défaut à confirmer"
    }
}

/** Au-delà, la ligne courte passerait la deuxième ligne à 1024 px. */
const LONGUEUR_MAX_LIGNE = 160

/**
 * Les actions avant d'envoyer de l'étude affichée, dans l'ordre servi, le
 * raccordement d'abord : une ligne par réserve de nature « action », jamais
 * la réserve du palier C5 ni le risque C5 (A7).
 */
export function pointsDActions(
    etude: OptimTurpeV2Etude | null | undefined
): PointDAction[] {
    if (!etude) return []
    const raccordement = texteDuRaccordement(etude)
    const points: PointDAction[] = []
    const vus = new Set<string>()
    for (const reserve of etude.reserves ?? []) {
        if (!reserve?.code || CODES_JAMAIS_POINT.has(reserve.code)) continue
        if (natureDe(reserve, raccordement) !== "action") continue
        if (vus.has(reserve.code)) continue
        vus.add(reserve.code)
        if (
            reserve.code === CODE_RACCORDEMENT_ECARTE ||
            reserve.code === CODE_BORNE_LEVEE
        ) {
            if (!raccordement) continue
            points.push({
                code: reserve.code,
                court: sansLeChemin(raccordement.court),
                detail: raccordement.avecRepli ? raccordement.complet : null,
                ancre: "raccordement",
                resume: resumeDuCode(reserve.code),
            })
            continue
        }
        const texte = (
            reserve.libelle_court?.trim() ||
            reserve.texte_complet?.trim() ||
            reserve.label?.trim() ||
            ""
        ).replace(/\s+/g, " ")
        if (!texte) continue
        const court = constatCourt(texte, LONGUEUR_MAX_LIGNE)
        points.push({
            code: reserve.code,
            court,
            detail: court === texte ? null : texte,
            // C5, changement récent : aucun champ ne le corrige (contrat §6 4.2).
            ancre:
                reserve.code === "modification-contractuelle-recente"
                    ? null
                    : reserve.code === CODE_BASE_IMPOSSIBLE
                      ? "structure"
                      : ancreDepuisChamp(reserve.champ),
            resume: resumeDuCode(reserve.code),
        })
    }
    // Une valeur écartée avec démarche servie sans réserve (étude ancienne) :
    // l'action reste dite.
    if (
        raccordement?.nature === "action" &&
        !points.some((p) => p.ancre === "raccordement")
    ) {
        points.unshift({
            code: CODE_RACCORDEMENT_ECARTE,
            court: sansLeChemin(raccordement.court),
            detail: raccordement.avecRepli ? raccordement.complet : null,
            ancre: "raccordement",
            resume: resumeDuCode(CODE_RACCORDEMENT_ECARTE),
        })
    }
    // Un même champ ne se corrige qu'une fois : une ligne par champ.
    const parChamp = points.filter(
        (p, i) =>
            p.ancre === null ||
            points.findIndex((q) => q.ancre === p.ancre) === i
    )
    // Le raccordement d'abord (il touche la conclusion).
    return [
        ...parChamp.filter((p) => p.ancre === "raccordement"),
        ...parChamp.filter((p) => p.ancre !== "raccordement"),
    ]
}

/** « 1 point · raccordement à corriger » ; `null` sans action (rangée absente). */
export function resumeDesPointsDActions(points: PointDAction[]): string | null {
    if (points.length === 0) return null
    const compte = points.length === 1 ? "1 point" : `${points.length} points`
    return `${compte} · ${points[0].resume}`
}

/** Les constats qui se lisent ailleurs que dans la ligne « Mesures ». */
const CONSTATS_LUS_AILLEURS = new Set([
    // Le raccordement : la ligne « Contrat d'acheminement » et la carte.
    CODE_RACCORDEMENT_ECARTE,
    CODE_BORNE_LEVEE,
    // Le risque C5 est le verdict ; le palier vit dans « Pourquoi 11 kVA » (A7).
    ...CODES_JAMAIS_POINT,
    // La période : la ligne « Période ».
    "meilleure-periode",
    "periode-disponible",
    // L'information « dépasse déjà », dite à part, sans étiquette (D20).
    "denivele-actuel-sous-la-pointe",
])

/**
 * Les constats de la ligne « Mesures » des hypothèses (contrat v1.2 §6 4.3,
 * 5.2 ; C9, D20 a) : la mention courte servie de chaque constat (lot 4f),
 * sinon son libellé servi, M9 tenu (jamais « extrapolation ») ; puis
 * l'information « le site dépasse déjà sa puissance », sans étiquette. Jamais
 * en orange, jamais un point à vérifier ; aucune réserve servie ne se perd
 * (fait 5).
 */
export function constatsDesMesures(
    etude: OptimTurpeV2Etude | null | undefined,
    { dejaDits = [] }: { dejaDits?: ReadonlyArray<string> } = {}
): { mentions: string[]; information: string | null } {
    if (!etude) return { mentions: [], information: null }
    const raccordement = texteDuRaccordement(etude)
    const mentions: string[] = []
    for (const reserve of etude.reserves ?? []) {
        if (!reserve?.code || CONSTATS_LUS_AILLEURS.has(reserve.code)) continue
        // Un constat que la ligne écrit déjà dans ses propres mots (l'arrêt
        // de la collecte, décision (n) du 20/10/2026) ne se redit pas.
        if (dejaDits.includes(reserve.code)) continue
        // Moteur v1.6 (contrat v1.6 §6 5.2) : un constat servi sur la ligne
        // « Période » ou sur celle du contrat actuel s'y lit, pas ici.
        if (reserve.ligne === "periode" || reserve.ligne === "contrat") continue
        if (natureDe(reserve, raccordement) !== "constat") continue
        const texte = (
            reserve.libelle_court?.trim() ||
            sansExtrapolation(reserve.label ?? "").trim()
        ).replace(/\.$/, "")
        if (texte && !mentions.includes(texte)) mentions.push(texte)
    }
    const depasse = (etude.reserves ?? []).find(
        (r) => r?.code === "denivele-actuel-sous-la-pointe"
    )
    const information =
        depasse?.label?.trim() && etude.compteur?.segment !== "C5"
            ? depasse.label.trim()
            : null
    return { mentions, information }
}
